import { gql, useMutation } from '@apollo/client';
import { Button, Card, CardBody, Heading } from '@apollo/orbit';
import { Telescope } from '@apollo/space-kit/illustrations/Telescope';
import React from 'react';
import { Link, useLocation } from 'react-router-dom';

import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';

import { GraphRef, graphRefToString } from '../../hooks/useGraphRef';
import {
  proposalChecksListRouteConfig,
  proposalChecksRouteConfig,
  proposalEditorRouteConfig,
} from '../../routes';

export const EmptyChecksPage = () => {
  const location = useLocation();

  return (
    <Card className="m-6 flex-1">
      <CardBody className="items-center justify-center">
        <Telescope />
        <Heading size="2xl" className="text-center">
          Catch issues as early as possible
        </Heading>
        <p className="mb-2 w-[65ch] text-center text-base text-secondary">
          Schema checks automatically run every time you save a revision.
        </p>
        <Button
          as={Link}
          to={proposalEditorRouteConfig.locationFrom({
            location,
            fromRouteConfig: proposalChecksRouteConfig,
          })}
        >
          Start editing
        </Button>
      </CardBody>
    </Card>
  );
};

export const RetryChecksPage = ({
  graphRef,
  latestRevisionId,
}: {
  graphRef: GraphRef;
  latestRevisionId: string;
}) => {
  const variantRef = graphRefToString(graphRef);
  const [retryCheckForProposalRevisionMutation] = useMutation<
    GraphQLTypes.RetryCheckForProposalRevisionMutation,
    GraphQLTypes.RetryCheckForProposalRevisionMutationVariables
  >(
    gql`
      mutation RetryCheckForProposalRevisionMutation(
        $input: ReRunCheckForRevisionInput!
        $variantRef: ID!
      ) {
        proposalByVariantRef(variantRef: $variantRef) {
          ... on ProposalMutation {
            reRunCheckForRevision(input: $input) {
              __typename
              ... on NotFoundError {
                message
              }
              ... on PermissionError {
                message
              }
              ... on Proposal {
                id
              }
              ... on ValidationError {
                message
              }
            }
          }
        }
      }
    `,
    {
      onCompleted: () => {
        window.location.reload();
      },
    },
  );

  return (
    <Card className="m-6 flex-1">
      <CardBody className="items-center justify-center">
        <Telescope />
        <Heading size="2xl" className="text-center">
          We cannot find a schema check for your changes.
        </Heading>
        <p className="mb-2 w-[65ch] text-center text-base text-secondary">
          Click here to submit a check workflow for the latest revision.
        </p>
        <Button
          onClick={() => {
            retryCheckForProposalRevisionMutation({
              variables: {
                input: {
                  revisionId: latestRevisionId,
                },
                variantRef,
              },
            });
          }}
        >
          Run check
        </Button>
      </CardBody>
    </Card>
  );
};

export const NoMatchChecksPage = () => {
  const location = useLocation();

  return (
    <Card className="m-6 flex-1">
      <CardBody className="items-center justify-center">
        <Telescope />
        <Heading size="2xl" className="text-center">
          No checks match your filters
        </Heading>
        <p className="mb-2 w-[65ch] text-center text-base text-secondary">
          Try clearing your filters to see checks
        </p>
        <Button
          as={Link}
          to={proposalChecksListRouteConfig.locationFrom({
            location,
            fromRouteConfig: proposalChecksListRouteConfig,
            patch: {
              author: RouteConfig.DELETE,
              status: RouteConfig.DELETE,
              subgraph: RouteConfig.DELETE,
            },
          })}
        >
          Clear filters
        </Button>
      </CardBody>
    </Card>
  );
};
